import { useStudyingDeckStudyCardsQueryOptions } from "@-/dev.improve.haniverse.lib.cards.read";
import { useIsFetching, useQuery } from "@-/com.npmjs.tanstack__react-query";
import {
  type QueuedGrapheme,
  useQueuedGraphemesQueryOptions,
} from "@-/dev.improve.haniverse.lib.learn.queue";
import type { MorphemeDetailsTileSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import type { DeckCardTile } from "@-/dev.improve.haniverse.routes.nav.app.deck._components.deck-card-section";
import { DeckLayoutContext } from "@-/dev.improve.haniverse.routes.nav.app.deck._context";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { useLayoutEffect, useMemo, useState } from "react";
import {
  localDayKey,
  startOfLocalDay,
  startOfNextLocalDay,
} from "@-/dev.improve.haniverse.lib.time.local-day";
import {
  deckUpcomingHourLabel,
  deckUpcomingRangeLabel,
  shiftDeckUpcomingMonth,
  shiftDeckUpcomingRange,
  type DeckUpcomingSelection,
  type DeckUpcomingTimeframe,
} from "#build-deck-upcoming-schedule";
import { DeckUpcomingCardsSurface } from "#deck-upcoming-cards-surface";
import { DeckUpcomingReviewChart } from "#deck-upcoming-review-chart";
import { DeckUpcomingScheduleLayout } from "#deck-upcoming-schedule-layout";
import { useDeckUpcomingPageModelSuspense } from "#use-deck-upcoming-page-model-suspense";

const EMPTY_QUEUED_GRAPHEMES: readonly QueuedGrapheme[] = [];
/** Fewer reviews than this show as detailed tiles by default (glyph tiles otherwise). */
const DETAILS_TILES_MAXIMUM_CARD_COUNT = 16;

export function DeckUpcomingView() {
  const [displayMode, setDisplayMode, setViewDisplayMode] = useContextSelectors(
    DeckLayoutContext,
    (c) => c.displayMode,
    (c) => c.setDisplayMode,
    (c) => c.setViewDisplayMode,
  );
  const [timeframe, setTimeframe] = useState<DeckUpcomingTimeframe>("week");
  const [rangeStartTime, setRangeStartTime] = useState<number | null>(null);
  // A pan keeps a month view's length so no bar resizes; other moves show calendar months.
  const [panPeriodCount, setPanPeriodCount] = useState<number | null>(null);
  // Selection belongs to an absolute bucket, never to a position in the visible chart.
  const [selection, setSelection] = useState<DeckUpcomingSelection>();
  const model = useDeckUpcomingPageModelSuspense({
    periodCount: panPeriodCount,
    rangeStartTime,
    selection,
    timeframe,
  });
  const selected = model.selected;
  const isRange = selection === undefined;
  const isUnscheduled = selection === "unscheduled";
  const isOverdue = selection === "overdue";
  // Several bars selected by holding one and dragging across the others.
  const isSpan = typeof selection === "object" && selection.endDate !== undefined;
  const selectedKey = isUnscheduled ? "unscheduled" : isOverdue ? "overdue" : selected.key;
  const rangeIncludesToday = model.periods.some((period) => period.key === model.todayKey);
  const selectedHour = isUnscheduled || isOverdue ? undefined : selected.hour;
  const isHour = selectedHour !== undefined;
  const today = new Date(`${model.todayKey}T00:00:00`);
  const selectionIncludesToday =
    typeof selection === "object" && !isHour && selected.date <= today && today < selected.endDate;
  const isPast = !isUnscheduled && !isOverdue && selected.endDate <= today;
  const spansPast = (isRange || isSpan) && selected.date < today;
  // Until the user picks a display mode, a short list of reviews shows as detailed tiles.
  const defaultDisplayMode =
    selected.cardCount < DETAILS_TILES_MAXIMUM_CARD_COUNT
      ? "morpheme-details-tiles"
      : "morpheme-glyph-tiles";
  useLayoutEffect(() => {
    setViewDisplayMode(defaultDisplayMode);
    return () => setViewDisplayMode(null);
  }, [defaultDisplayMode, setViewDisplayMode]);
  const isRefetching =
    useIsFetching({ exact: true, queryKey: useStudyingDeckStudyCardsQueryOptions().queryKey }) > 0;
  const queuedGraphemes = useQuery(useQueuedGraphemesQueryOptions()).data ?? EMPTY_QUEUED_GRAPHEMES;
  const queued = useMemo(
    () => ({
      cardCount: queuedGraphemes.length,
      coreSliceByFront: new Map(
        queuedGraphemes.map(({ grapheme }): [string, MorphemeDetailsTileSlice] => [
          grapheme,
          { front: grapheme, morphemeType: "grapheme", pinyinList: [], tier: null },
        ]),
      ),
      fronts: queuedGraphemes.map(({ grapheme }) => grapheme),
      tilesByFront: new Map(
        queuedGraphemes.map(({ grapheme, userCardId }): [string, DeckCardTile[]] => [
          grapheme,
          [
            {
              cardType: "meaning",
              contentKind: "grapheme-meaning",
              due: null,
              pinyin: null,
              userCardId,
            },
          ],
        ]),
      ),
    }),
    [queuedGraphemes],
  );
  const dateLabel = selected.date.toLocaleDateString(undefined, {
    day: "numeric",
    month: "long",
    weekday: "long",
    year:
      selected.date.getFullYear() === new Date(`${model.todayKey}T00:00:00`).getFullYear()
        ? undefined
        : "numeric",
  });
  const isWithinOneDay =
    localDayKey(selected.date) === localDayKey(new Date(selected.endDate.getTime() - 1));
  const title = isUnscheduled
    ? "Unscheduled"
    : isOverdue
      ? "Overdue"
      : isRange
        ? deckUpcomingRangeLabel(model.rangeStart, model.rangeEnd, timeframe)
        : isHour && isWithinOneDay
          ? `${dateLabel} · ${deckUpcomingHourLabel(selected)}`
          : isSpan
            ? deckUpcomingRangeLabel(selected.date, selected.endDate, isHour ? "day" : "week")
            : dateLabel;
  const countLabel = isUnscheduled
    ? "Unscheduled"
    : isOverdue
      ? "Overdue"
      : isPast
        ? "Past"
        : spansPast
          ? "Upcoming/Past"
          : "Upcoming";
  // A quiz has no glyph and so no tile: say how many of the counted cards are quizzes.
  const quizSubtitle =
    selected.quizCount === 0
      ? undefined
      : `Includes ${selected.quizCount} ${selected.quizCount === 1 ? "quiz" : "quizzes"}`;
  return (
    <DeckUpcomingScheduleLayout
      chart={(onControlsHeightChange) => (
        <DeckUpcomingReviewChart
          adjacentPeriods={model.adjacentPeriods}
          // Hourly windows key their bars by instant, so compare the window with today itself.
          isTodayInView={model.rangeStart < startOfNextLocalDay(today) && model.rangeEnd > today}
          maximumCardCount={model.maximumCardCount}
          onControlsHeightChange={onControlsHeightChange}
          onNavigate={(direction) => {
            setPanPeriodCount(null);
            setRangeStartTime((previous) => {
              const date = previous === null ? model.rangeStart : new Date(previous);
              return (
                timeframe === "month"
                  ? shiftDeckUpcomingMonth(date, direction)
                  : shiftDeckUpcomingRange(
                      date,
                      timeframe,
                      direction * (timeframe === "week" ? 7 : 24),
                    )
              ).getTime();
            });
          }}
          // The chart already moved its viewport to the settled window on the UI thread.
          onPan={(start, periodCount) => {
            setRangeStartTime(start);
            setPanPeriodCount(timeframe === "month" ? periodCount : null);
          }}
          onSelect={(key, toggle) => {
            if (toggle && key === selectedKey) {
              setSelection(undefined);
              return;
            }
            if (key === "unscheduled" || key === "overdue") {
              setSelection(key);
              return;
            }
            const period = [
              ...model.adjacentPeriods.before,
              ...model.periods,
              ...model.adjacentPeriods.after,
            ].find((period) => period.key === key);
            if (period !== undefined) {
              setSelection({ date: period.date, hour: period.hour });
            }
          }}
          onSelectSpan={(fromKey, toKey) => {
            const all = [
              ...model.adjacentPeriods.before,
              ...model.periods,
              ...model.adjacentPeriods.after,
            ];
            const from = all.findIndex((period) => period.key === fromKey);
            const to = all.findIndex((period) => period.key === toKey);
            const first = all[Math.min(from, to)];
            const last = all[Math.max(from, to)];
            if (from === -1 || to === -1 || first === undefined || last === undefined) {
              return;
            }
            setSelection(
              first === last
                ? { date: first.date, hour: first.hour }
                : { date: first.date, endDate: last.endDate, hour: first.hour },
            );
          }}
          onTimeframeChange={(nextTimeframe) => {
            if (nextTimeframe === timeframe) {
              return;
            }
            setTimeframe(nextTimeframe);
            setPanPeriodCount(null);
            // Zoom the visible window without replacing the selected cards.
            const date = startOfLocalDay(model.rangeStart);
            if (nextTimeframe === "month") {
              date.setDate(1);
            }
            setRangeStartTime(date.getTime());
          }}
          // The default window starts today (this month in the month view); the selection stays.
          onToday={() => {
            setRangeStartTime(null);
            setPanPeriodCount(null);
          }}
          overdueCount={model.overdue.cardCount}
          periods={model.periods}
          rangeEnd={model.rangeEnd}
          rangeStart={model.rangeStart}
          selectedKey={selectedKey}
          selectedSpan={typeof selection === "object" ? selected : undefined}
          timeframe={timeframe}
          todayKey={model.todayKey}
          unscheduledCount={model.unscheduled.cardCount}
        />
      )}
      // The chart chooses the cards; a new choice keeps the sheet at its current height.
      contentKey={`${selectedKey}:${model.rangeStart.getTime()}:${timeframe}`}
    >
      {(bottomInset, onRowBoundariesChange, scrollOffset) =>
        selected === undefined ? null : (
          <DeckUpcomingCardsSurface
            bottomInset={bottomInset}
            key={selectedKey}
            onRowBoundariesChange={onRowBoundariesChange}
            queued={
              queued.cardCount > 0 && (selectionIncludesToday || (isRange && rangeIncludesToday))
                ? {
                    ...queued,
                    displayMode,
                    emptyMessage: "Nothing queued.",
                    stageByFront: model.stageByCharacter,
                    subtitle: "Waiting to be added to the review schedule",
                    title: "Queued",
                  }
                : undefined
            }
            reviews={{
              cardCount: selected.cardCount,
              coreSliceByFront: model.coreSliceByCharacter,
              countLabel,
              displayMode,
              emptyMessage:
                selected.quizCount > 0
                  ? "Only quizzes, which have no tiles."
                  : isPast
                    ? "No past reviews in this range."
                    : isRange || isSpan
                      ? spansPast
                        ? "No reviews in this range."
                        : "No reviews due in this range."
                      : isUnscheduled
                        ? "No unscheduled cards."
                        : isOverdue
                          ? "No overdue reviews."
                          : isHour
                            ? "No reviews due this hour."
                            : "No reviews due this day.",
              fronts: selected.fronts,
              isRefetching,
              onToggleDetails: () =>
                setDisplayMode(
                  displayMode === "morpheme-details-tiles"
                    ? "morpheme-glyph-tiles"
                    : "morpheme-details-tiles",
                ),
              stageByFront: model.stageByCharacter,
              subtitle: quizSubtitle,
              tilesByFront: selected.tilesByFront,
              title,
            }}
            scrollOffset={scrollOffset}
          />
        )
      }
    </DeckUpcomingScheduleLayout>
  );
}
